import React, { FC, useEffect, useState } from 'react';
import styles from './styles.module.css';
import { Document, Page } from 'appJS/utils/pdfjs_worker';
import cx from 'classnames';
import camelcaseKeys from 'camelcase-keys';
import {
  BrowserRouter,
  Route,
  useNavigate,
  useParams,
  useLocation,
  Routes
} from 'react-router-dom';
import NavBar from './NavBar';
import useDesignFeedback from './useDesignFeedback';
import useDesignGroupNavigation from './useDesignGroupNavigation';
import Controls from './Controls';
import useMediaQuery from '../../../../clients/sidebar/views/CreateTask/create_task/useMediaQuery';
import { ShareModal } from '../../../clients/design_assets/ShareModal';
import { ArchivedBanner } from 'jsViews/design_feedback/ArchivedBanner/ArchivedBanner';
import { ShareProject } from '../share_project';
import { Props as SharingProps } from '../share_project/types';
import { AntdWrapper } from '../../../clients/shared/AntdWrapper';
import { Spin } from 'antd';
import {
  OrganizationProvider,
  useOrganizationState
} from '../../../../clients/providers/Organization';
import DesignGroupNavigation, {
  DesignGroupNavigationAsset
} from './DesignGroupNavigation';
import { versionsForAsset } from './DesignGroupNavigation/utils';
import { DocumentCallback } from 'react-pdf/dist/cjs/shared/types';
import VersionSelector from '../../../clients/design_assets/VersionSelector';
import { AssetVersion } from '../../../clients/design_assets/types';
import { ASSET_VERSIONING_EXPERIMENT } from '../../../clients/design_assets/AssetVersioningContext';
import {
  ASSET_VERSION_PARAM,
  ASSET_VERSION_SELECT_EVENT,
  getExplicitAssetVersion
} from '../../../../clients/shared/utils/assetTaskVersion';
type Props = {
  asset: any;
  groupAssets?: any[];
  project: any;
  viewKanbanProject: boolean;
  bugherdUrl: string;
  page?: string;
  loggedIn: boolean;
  projectOwner: {
    name: string;
    email: string;
    id: number;
  } | null;
  hasAccess: boolean;
  skipAccessCheck: boolean;
  isGuest: boolean;
  sharingProps: SharingProps;
  currentUser: Object;
};

const DesignFeedback: FC<Props> = ({
  asset,
  groupAssets = [],
  project,
  viewKanbanProject,
  bugherdUrl,
  page,
  loggedIn,
  projectOwner,
  hasAccess,
  skipAccessCheck,
  isGuest,
  sharingProps,
  currentUser
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { assetId } = useParams();
  const isMobileView = useMediaQuery('(max-width: 470px)');
  const container = document.getElementById(
    'feedback_router_container'
  ) as HTMLDivElement;
  const [hasMounted, setHasMounted] = useState<boolean>(false);
  const [isVersionSwitching, setIsVersionSwitching] = useState<boolean>(false);

  project = camelcaseKeys(project, { deep: true });
  const {
    groupNavigationCollapsed,
    setGroupNavigationCollapsed,
    selectedAsset,
    setSelectedAsset,
    groupNavigationAssets,
    showGroupNavigation,
    groupNavigationWidth
  } = useDesignGroupNavigation({
    asset,
    groupAssets,
    assetId,
    setHasMounted
  });
  const { hasOrganizationExperiment } = useOrganizationState();
  const versioningEnabled = hasOrganizationExperiment(
    ASSET_VERSIONING_EXPERIMENT
  );
  const versions: AssetVersion[] = versioningEnabled
    ? selectedAsset.versions || []
    : [];
  const versionFromUrl = versioningEnabled
    ? getExplicitAssetVersion(`${location.pathname}${location.search}`)
    : null;
  const fallbackVersion: AssetVersion = {
    version: 1,
    url: selectedAsset.url,
    fileName: selectedAsset.fileName || selectedAsset.name,
    fileType: selectedAsset.fileType,
    createdAt: selectedAsset.createdAt
  };
  const resolvedVersion =
    versions.find(version => version.version === versionFromUrl) ||
    versions[versions.length - 1] ||
    fallbackVersion;
  const [selectedVersion, setSelectedVersion] = useState<
    AssetVersion | undefined
  >(resolvedVersion);
  useEffect(() => {
    if (!versioningEnabled) return;
    setSelectedVersion(current =>
      current?.version === resolvedVersion.version &&
      current?.url === resolvedVersion.url
        ? current
        : resolvedVersion
    );
    setIsVersionSwitching(false);
  }, [
    versioningEnabled,
    selectedAsset.id,
    resolvedVersion.version,
    resolvedVersion.url
  ]);
  const displayedAsset = versioningEnabled
    ? {
        ...selectedAsset,
        url: resolvedVersion.url || selectedAsset.url,
        fileName: resolvedVersion.fileName || selectedAsset.fileName,
        fileType: resolvedVersion.fileType || selectedAsset.fileType
      }
    : selectedAsset;

  const {
    imageRef,
    canvasRef,
    sidebarClosed,
    useOriginalSize,
    onUseOriginalSize,
    currentPage,
    setCurrentPage,
    totalPages,
    fileName,
    isPdf,
    url,
    handleZoomIn,
    handleZoomOut,
    handleLoadSuccess,
    fitToPage,
    onFitToPage,
    pdfScale,
    isOverflowing,
    inlineImageWidth,
    onImageLoad,
    shareItem,
    setShareItem,
    onSharingClick,
    naturalWidth
  } = useDesignFeedback({
    page,
    project,
    asset: displayedAsset,
    navigate,
    bugherdUrl,
    isMobileView,
    horizontalSpacing: groupNavigationWidth
  });

  useEffect(() => {
    if (!naturalWidth || hasMounted) return;

    const timeout = setTimeout(() => setHasMounted(true), 400);

    return () => clearTimeout(timeout);
  }, [naturalWidth, hasMounted, selectedAsset.id]);

  const isArchived = selectedAsset.status === 'archived';

  const assetPath = `/projects/${project.id}/assets/${selectedAsset.id}`;

  const onSelectAsset = (
    nextAsset: DesignGroupNavigationAsset,
    preferredVersion?: number
  ) => {
    const nextVersions = nextAsset.versions || [];
    const preferred =
      versioningEnabled && preferredVersion
        ? nextVersions.find(version => version.version === preferredVersion)
        : undefined;

    if (nextAsset.id !== selectedAsset.id) {
      setSelectedAsset(nextAsset);
    } else if (!preferred) {
      return;
    }

    if (versioningEnabled) {
      const nextVersion =
        preferred ||
        nextVersions[nextVersions.length - 1] || {
          version: 1,
          url: nextAsset.url,
          fileName: nextAsset.fileName || nextAsset.name || '',
          fileType: nextAsset.fileType || '',
          createdAt: fallbackVersion.createdAt
        };
      setIsVersionSwitching(false);
      setSelectedVersion(nextVersion);
      navigate(
        `/projects/${project.id}/assets/${nextAsset.id}${
          preferred ? `?${ASSET_VERSION_PARAM}=${preferred.version}` : ''
        }`
      );
      return;
    }

    navigate(`/projects/${project.id}/assets/${nextAsset.id}`);
  };

  const onSelectVersion = (version: AssetVersion) => {
    if (!versioningEnabled) return;
    const params = new URLSearchParams(window.location.search);
    params.set(ASSET_VERSION_PARAM, String(version.version));
    if (selectedVersion && version.version !== selectedVersion.version) {
      setIsVersionSwitching(true);
    }
    setSelectedVersion(version);
    navigate(`${assetPath}?${params}`);
  };

  const onGroupVersionChange = (version: AssetVersion | 'all') => {
    if (!versioningEnabled || version === 'all') return;
    const versions = versionsForAsset(selectedAsset);
    const nextVersion =
      versions.find(item => item.version === version.version) ||
      versions.find(item => item.version === 1) ||
      versions[0];
    if (nextVersion) onSelectVersion(nextVersion);
  };

  useEffect(() => {
    if (!versioningEnabled) return undefined;

    const selectTaskVersion = (event: Event) => {
      const versionNumber = (event as CustomEvent<{ version: number }>).detail
        .version;
      const version =
        versions.find(item => item.version === versionNumber) ||
        versions[versions.length - 1] ||
        fallbackVersion;
      onSelectVersion(version);
    };

    window.addEventListener(ASSET_VERSION_SELECT_EVENT, selectTaskVersion);
    return () =>
      window.removeEventListener(ASSET_VERSION_SELECT_EVENT, selectTaskVersion);
  }, [versioningEnabled, selectedAsset.id, selectedAsset.versions]);

  const refreshPins = () => {
    // @ts-expect-error sidebar code owns the optional pin refresh hook
    window.bugherd?.refreshPins?.();
  };

  const finishVersionSwitch = () => {
    if (!versioningEnabled) return;
    requestAnimationFrame(() =>
      requestAnimationFrame(() => setIsVersionSwitching(false))
    );
  };

  const onSelectedImageLoad = () => {
    onImageLoad();
    refreshPins();
    finishVersionSwitch();
  };

  const onSelectedPdfLoadSuccess = async (success: DocumentCallback) => {
    await handleLoadSuccess(success);
    refreshPins();
    finishVersionSwitch();
  };

  const assetPreview = isPdf ? (
    <Document
      key={
        versioningEnabled
          ? `${selectedAsset.id}-${selectedVersion?.version || 1}`
          : selectedAsset.id
      }
      file={url}
      onLoadSuccess={onSelectedPdfLoadSuccess}
      onLoadError={
        versioningEnabled ? () => setIsVersionSwitching(false) : undefined
      }
      loading={null}
    >
      <Page
        canvasRef={canvasRef}
        pageNumber={currentPage}
        renderTextLayer={false}
        renderAnnotationLayer={false}
        scale={pdfScale}
      />
    </Document>
  ) : (
    <img
      key={
        versioningEnabled
          ? `${selectedAsset.id}-${selectedVersion?.version || 1}`
          : selectedAsset.id
      }
      onLoad={onSelectedImageLoad}
      onError={
        versioningEnabled ? () => setIsVersionSwitching(false) : undefined
      }
      ref={imageRef}
      style={{
        ...inlineImageWidth,
        ...(versioningEnabled ? { transition: 'none' } : {})
      }}
      className={cx(styles.asset, { [styles.unmounted]: !hasMounted })}
      src={url}
      alt="design_asset_image"
      crossOrigin="anonymous"
    />
  );

  return (
    <div
      className={cx(styles.designFeedbackContainer, 'designFeedbackContainer', {
        isPdf: isPdf,
        isNotPdf: !isPdf,
        sidebarClosed: sidebarClosed,
        sidebarOpen: !sidebarClosed,
        noSidebar: !loggedIn || !hasAccess,
        [styles.noSizeTransition]: versioningEnabled
      })}
    >
      <ShareProject {...sharingProps} isGuest={isGuest} container={container} />
      <ShareModal
        open={!!shareItem}
        closeModal={() => setShareItem(null)}
        shareURL={`${bugherdUrl}/projects/${project.id}/assets/${selectedAsset.id}`}
        canInviteToProject={viewKanbanProject}
        container={container}
        inviteEndpoint={`/projects/${project.id}/kanban?open=invite_team`}
      />
      <div
        className={cx(styles.pageHeader, { [styles.fullHead]: sidebarClosed })}
      >
        <NavBar
          project={project}
          fileName={fileName}
          viewKanbanProject={viewKanbanProject}
          loggedIn={loggedIn}
          hasAccess={hasAccess}
          versionSelector={
            versioningEnabled && (
              <VersionSelector
                versions={versions.length ? versions : [resolvedVersion]}
                selected={selectedVersion || resolvedVersion}
                onSelect={onSelectVersion}
                container={container}
              />
            )
          }
        />
        <Controls
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalPages={totalPages}
          container={container}
          useOriginalSize={useOriginalSize}
          onUseOriginalSize={onUseOriginalSize}
          isPdf={isPdf}
          handleZoomIn={handleZoomIn}
          handleZoomOut={handleZoomOut}
          onShareClick={onSharingClick}
          fitToPage={fitToPage}
          onFitToPage={onFitToPage}
          loggedIn={loggedIn}
        />
      </div>
      <div className={styles.content}>
        {showGroupNavigation && (
          <DesignGroupNavigation
            assets={groupNavigationAssets}
            collapsed={groupNavigationCollapsed}
            selectedAssetId={selectedAsset.id}
            onSelect={onSelectAsset}
            onToggle={() =>
              setGroupNavigationCollapsed(collapsed => !collapsed)
            }
            versioningEnabled={versioningEnabled}
            container={container || undefined}
            onGroupVersionChange={onGroupVersionChange}
          />
        )}
        <div
          className={cx(styles.assetContainer, {
            [styles.overflowing]: isOverflowing
          })}
        >
          {versioningEnabled ? (
            <div
              className={cx(styles.assetStage, {
                [styles.versionSwitching]: isVersionSwitching
              })}
            >
              {assetPreview}
            </div>
          ) : (
            assetPreview
          )}
          {!hasMounted && (
            <Spin
              wrapperClassName={styles.loaderWrapper}
              size="large"
              className={styles.loader}
            >
              <h2>Loading your file...</h2>
              <p>Hold on tight.</p>
            </Spin>
          )}
        </div>
      </div>

      {hasAccess && isArchived && (
        <ArchivedBanner
          container={container}
          projectId={project.id}
          isGuest={isGuest}
        />
      )}
    </div>
  );
};

const FeedbackRouter = props => {
  return (
    <BrowserRouter>
      <Routes>
        <Route
          path={`/projects/${props.project.id}/assets/:assetId`}
          element={
            <AntdWrapper>
              <OrganizationProvider id={props.organization.id}>
                <DesignFeedback {...props} />
              </OrganizationProvider>
            </AntdWrapper>
          }
        />
      </Routes>
    </BrowserRouter>
  );
};

export default FeedbackRouter;
